import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import MapboxGlobe from '../../components/MapboxGlobe/MapboxGlobe';
import {
  useWeatherFactorApi,
  WeatherFactorResponse,
  WeatherFactorLeg,
  AverageWeatherFactor,
  MONTH_KEYS,
  MONTH_NAMES,
  MonthlySpeedloss
} from '../../api/ApiWeatherFactor/ApiWeatherFactor';
import './WeatherFactor.scss';

const WeatherFactor: React.FC = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const id = query.get('id');
  const src = query.get('src');

  const { fetchWeatherFactor } = useWeatherFactorApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<WeatherFactorResponse | null>(null);

  useEffect(() => {
    if (!id) return;

    const loadData = async () => {
      setLoading(true);
      setError(null);

      const result = await fetchWeatherFactor(id, src);

      if (result.ok && result.data) {
        setData(result.data);
      } else {
        setError(result.message || 'Failed to load weather factor data');
      }

      setLoading(false);
    };

    loadData();
  }, [id, src, fetchWeatherFactor]);

  // Check if a month is active for highlighting
  const isMonthActive = (activeMonths: string[], monthKey: keyof MonthlySpeedloss): boolean => {
    const monthName = MONTH_NAMES[monthKey];
    return activeMonths.some(m =>
      m.toLowerCase() === monthName.toLowerCase() ||
      m.toLowerCase() === monthKey
    );
  };

  if (!id) {
    return (
      <div className="weather-factor-container">
        <div className="wf-message wf-error">
          <i className="bi bi-exclamation-triangle"></i>
          <p>No voyage ID provided. Please provide an ID in the URL.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="weather-factor-container">
        <div className="wf-message wf-loading">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading weather factor data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="weather-factor-container">
        <div className="wf-message wf-error">
          <i className="bi bi-exclamation-triangle"></i>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="weather-factor-container">
      <div className="wf-header">
        <h1>Weather Factor Analysis</h1>
        <p className="wf-subtitle">Voyage ID: {id}</p>
      </div>

      {data && (
        <>
          {/* Top section: Summary + Globe side by side */}
          <section className="wf-top-section">
            {/* Average Weather Factor Summary - Left */}
            <div className="wf-summary-panel">
              <h2>Average Weather Factor</h2>
              <div className="table-responsive">
                <table className="wf-table wf-summary-table">
                  <thead>
                    <tr>
                      <th>Route</th>
                      <th>Condition</th>
                      <th>VoyCalc NM</th>
                      <th>IMOS NM</th>
                      <th>Avg WF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.avgwf.map((avg: AverageWeatherFactor, idx: number) => (
                      <tr key={idx}>
                        <td className="route-cell">{avg.route}</td>
                        <td className={`condition-${avg.type}`}>{avg.type}</td>
                        <td>{avg.voycalc_nm.toFixed(0)}</td>
                        <td>{avg.imos_nm.toFixed(0)}</td>
                        <td className="wf-value">{avg.wf.toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {data.avgwf.some(avg => Math.abs(avg.voycalc_nm - avg.imos_nm) > 250) && (
                <div className="wf-warning">
                  <i className="bi bi-exclamation-triangle-fill"></i>
                  <span>Distance deviation detected ({'>'}250 NM). Please verify that the route is correct.</span>
                </div>
              )}
            </div>

            {/* Globe Map - Right */}
            <div className="wf-globe-panel">
              <MapboxGlobe
                waypoints={data.wp}
                height="400px"
              />
            </div>
          </section>

          {/* Weather Factor per Leg per Month Table - Full width below */}
          <section className="wf-section">
            <h2>Weather Factor per Leg (Monthly)</h2>
            <div className="table-responsive">
              <table className="wf-table wf-monthly-table">
                <thead>
                  <tr>
                    <th className="sticky-col">Route</th>
                    <th>Condition</th>
                    <th>NM</th>
                    {MONTH_KEYS.map(key => (
                      <th key={key}>{MONTH_NAMES[key]}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.wf.map((leg: WeatherFactorLeg, idx: number) => (
                    <tr key={idx}>
                      <td className="sticky-col route-cell">{leg.route}</td>
                      <td className={`condition-${leg.condition}`}>{leg.condition}</td>
                      <td>{leg.nm.toFixed(0)}</td>
                      {MONTH_KEYS.map(monthKey => (
                        <td
                          key={monthKey}
                          className={isMonthActive(leg.activeMonths, monthKey) ? 'active-month' : ''}
                        >
                          {leg.speedloss[monthKey].toFixed(1)}%
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="table-note">
              <span className="active-indicator"></span> Highlighted months indicate voyage period (based on ETD/ETA)
            </p>
          </section>
        </>
      )}
    </div>
  );
};

export default WeatherFactor;
